import {
  MarketingShell,
  Seo,
  Container,
  Section,
  PageHero,
  SectionIntro,
  Accordion,
  Reveal,
  CTASection,
} from './kit.jsx'

const PRODUCT = [
  {
    q: 'What exactly is Revailo Sales?',
    a: 'Revailo Sales is a lead engine built for B2B teams. It combines a verified database of 10,942 companies with multi-step email outreach, a deal-flow pipeline, activity and analytics, and an in-app assistant — so your team can go from finding an account to closing the deal without leaving one workspace.',
  },
  {
    q: 'Where does the lead data come from?',
    a: 'Every company comes from our verified U.S. business directory. We ingest the full dataset, clean it, de-duplicate it, and organize it by industry and region — including company name, location, service area, established year, size, annual volume, and named contacts.',
  },
  {
    q: 'How many leads are included, and what is in each record?',
    a: 'Your workspace ships with all 10,942 verified companies. Each record carries the firm name, region, industry, service area, size, annual volume, and contact details, presented in sortable, searchable tables so reps can filter to their best-fit accounts in seconds.',
  },
  {
    q: 'Which industries and regions do you cover?',
    a: 'One vertical today — masonry contractors, across regions in California, Texas, Florida, and Illinois — and the platform is built to add more: a new vertical is one config entry plus its data. You can scope any rep to a single territory or open up the whole country, and filter within a region by company size and annual volume so nobody wastes time on accounts that are not worth the call.',
  },
  {
    q: 'How current is the data?',
    a: 'The database is refreshed against our sources on an ongoing basis, and companies you touch stay live in your workspace — contacts, sequence status, and pipeline stage update in real time as your team works them. Any region loads in under a second, so you are always looking at the current picture, not a stale spreadsheet.',
  },
]

const OUTREACH = [
  {
    q: 'How do the outreach sequences work?',
    a: 'You build Apollo-style cadences — an intro email, a wait, a follow-up with a case study, another wait, a break-up email — and drop the accounts you want into them. Revailo Sales sends each step on schedule, pauses a contact the moment they reply, and logs every send and response on that company\'s timeline.',
  },
  {
    q: 'Does it send from my own Gmail?',
    a: 'Yes. You connect your Gmail account once, and every email goes out as you, from your real address. Replies land back in your normal inbox and are mirrored into the account timeline in Revailo Sales — there is no separate sending domain and no shared blast address that hurts your reputation.',
  },
  {
    q: 'How do you protect deliverability and keep me out of spam?',
    a: 'Because you send through your own authenticated Gmail, messages carry your existing sending reputation. Sequences pace sends rather than firing all at once, stop automatically on a reply, and honor opt-outs — so your outreach reads like a person following up, not a bulk campaign.',
  },
  {
    q: 'Can I import my own list of contacts?',
    a: 'You can. Alongside the built-in verified database, you can bring accounts and contacts you already work and run them through the same sequences, pipeline, and analytics. Your list lives in your workspace next to the verified data, not in a separate silo.',
  },
]

const PRICING = [
  {
    q: 'How much does Revailo Sales cost, and how are seats priced?',
    a: 'Plans are billed per workspace with a set number of included seats, and you can add reps as your team grows. The full 10,942-company database and the core outreach, pipeline, and analytics tools are included on every paid plan — see the pricing page for current tiers, or talk to sales for a larger team.',
  },
  {
    q: 'Is there a free trial?',
    a: 'Yes — you can start free, no credit card required, and see every qualified company in your market on day one. You get hands-on time with the real database and the sequence builder before you decide to upgrade.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Absolutely. Plans are month-to-month with no long-term contract, and you can cancel from your account settings whenever you like. There is no data project to unwind and no penalty for leaving.',
  },
]

const SECURITY = [
  {
    q: 'Who owns the data, and is it secure?',
    a: 'The accounts you work, the contacts you import, your sequences, and your pipeline are yours. Access is protected by Amazon Cognito authentication, data is stored in per-category tables scoped to your workspace, and team access is governed by roles so people only see what they should.',
  },
  {
    q: 'Can I export or delete my data?',
    a: 'Yes. You can export your pipeline and activity at any time, and you can request deletion of your workspace data on cancellation. We remove your outreach history, imported contacts, and pipeline records on request — the shared directory simply returns to its default state for other customers.',
  },
]

const GETTING_STARTED = [
  {
    q: 'How long does setup take before I am sending?',
    a: 'An afternoon, not a quarter. There is no data import or long onboarding — sign in and your market is already loaded. Connect Gmail, build a sequence, add the accounts you want, and you can have real outreach going the same day.',
  },
  {
    q: 'Can my whole team use it, and what support do I get?',
    a: 'Revailo Sales is built for teams. Invite reps, assign regions, and keep everyone working the same source of truth with role-based collaboration and light/dark themes for how each person likes to work. Support is available by email, and larger teams get a guided onboarding to get every rep productive fast.',
  },
]

const ALL_FAQS = [...PRODUCT, ...OUTREACH, ...PRICING, ...SECURITY, ...GETTING_STARTED]

const FAQ_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: ALL_FAQS.map((it) => ({
    '@type': 'Question',
    name: it.q,
    acceptedAnswer: { '@type': 'Answer', text: it.a },
  })),
}

export default function FaqPage() {
  return (
    <MarketingShell active="">
      <Seo
        title="FAQ"
        description="Answers to common questions about Revailo Sales — the data behind 10,942 verified B2B leads, how Gmail outreach works, pricing, security, and getting started."
        path="/faq"
        keywords="Revailo Sales FAQ, b2b leads questions, pricing, Gmail outreach deliverability"
        jsonLd={FAQ_JSON_LD}
      />

      <Section>
        <PageHero
          eyebrow="FAQ"
          title="Questions, answered."
          lead="Everything teams ask before they start — where the 10,942 leads come from, how outreach sends from your own Gmail, what it costs, and how your data is handled."
          primary={{ label: 'Start free', to: '/signup' }}
          secondary={{ label: 'Talk to sales', to: '/contact' }}
        />
      </Section>

      <Section sm id="product">
        <Container>
          <Reveal>
            <SectionIntro
              eyebrow="Product & data"
              title="What you get and where it comes from"
              lead="The database, the trades, the regions, and how fresh it stays."
              align="left"
            />
          </Reveal>
          <div className="mt-10">
            <Reveal>
              <Accordion items={PRODUCT} />
            </Reveal>
          </div>
        </Container>
      </Section>

      <Section sm id="outreach">
        <Container>
          <Reveal>
            <SectionIntro
              eyebrow="Outreach"
              title="Sending from your own inbox"
              lead="How sequences run, why they send through your Gmail, and how deliverability holds up."
              align="left"
            />
          </Reveal>
          <div className="mt-10">
            <Reveal>
              <Accordion items={OUTREACH} />
            </Reveal>
          </div>
        </Container>
      </Section>

      <Section sm id="pricing">
        <Container>
          <Reveal>
            <SectionIntro
              eyebrow="Pricing & plans"
              title="What it costs and how billing works"
              lead="Seats, the free trial, and cancelling whenever you want."
              align="left"
            />
          </Reveal>
          <div className="mt-10">
            <Reveal>
              <Accordion items={PRICING} />
            </Reveal>
          </div>
        </Container>
      </Section>

      <Section sm id="security">
        <Container>
          <Reveal>
            <SectionIntro
              eyebrow="Security & data"
              title="Who owns it and how it is protected"
              lead="Ownership, authentication, export, and deletion."
              align="left"
            />
          </Reveal>
          <div className="mt-10">
            <Reveal>
              <Accordion items={SECURITY} />
            </Reveal>
          </div>
        </Container>
      </Section>

      <Section sm id="getting-started">
        <Container>
          <Reveal>
            <SectionIntro
              eyebrow="Getting started"
              title="From sign-in to first send"
              lead="Setup time, team collaboration, and the support behind you."
              align="left"
            />
          </Reveal>
          <div className="mt-10">
            <Reveal>
              <Accordion items={GETTING_STARTED} />
            </Reveal>
          </div>
        </Container>
      </Section>

      <CTASection
        eyebrow="Still deciding?"
        title="See your market before you commit to anything."
        body="Start free and browse every qualified company in your region today — no credit card, no data project. Or talk to us and we will walk you through it."
        primary={{ label: 'Start free', to: '/signup' }}
        secondary={{ label: 'Talk to sales', to: '/contact' }}
      />
    </MarketingShell>
  )
}
